import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  SimpleGrid,
  Box,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  Textarea,
  Wrap,
  WrapItem,
  Tag,
  Text,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { mealTags } from '../utils/constants';

interface MealPreferencesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formData: any; // Use any for now to avoid circular type issues
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const MealPreferencesDialog: React.FC<MealPreferencesDialogProps> = ({ 
  isOpen, 
  onClose, 
  formData, 
  setFormData
}) => {
  
  const selectedBg = useColorModeValue('mamaFuel.moss.700', 'mamaFuel.moss.600');
  const selectedColor = useColorModeValue('white', 'white');
  const unselectedBg = useColorModeValue('white', 'gray.100');
  const unselectedColor = useColorModeValue('black', 'gray.800');
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  // Function to toggle a meal tag
  const toggleMealTag = (tagName: string): void => {
    setFormData((prev: any) => {
      // Initialize mealTags if it doesn't exist
      const currentMealTags = prev.mealTags || [];
      
      const exists = currentMealTags.some((tag: any) => 
        tag.name === tagName
      );
      
      if (exists) {
        // Remove the tag
        return {
          ...prev,
          mealTags: currentMealTags.filter((tag: any) => 
            tag.name !== tagName
          )
        };
      } else {
        // Add the tag
        return {
          ...prev,
          mealTags: [
            ...currentMealTags,
            { name: tagName, specifics: '' }
          ]
        };
      }
    });
  };

  const handleSave = (): void => {
    onClose();
  };

  const isTagSelected = (tagName: string): boolean => {
    const currentMealTags = formData.mealTags || [];
    return currentMealTags.some((tag: { name: string }) => tag.name === tagName);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Meal Suggestions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing="6" align="stretch">
            <Box>
              <Text>
                Select meal tags to help friends know what types of food you'd enjoy! 
                These are just suggestions - friends can bring anything.
              </Text>
            </Box>
            
            {/* Meal Tags Selection */}
            <Box>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 4}} spacing={3}>
                {mealTags.map((tag) => (
                  <Button
                    key={tag.name}
                    size="sm"
                    variant="outline"
                    bg={isTagSelected(tag.name) ? selectedBg : unselectedBg}
                    color={isTagSelected(tag.name) ? selectedColor : unselectedColor}
                    borderColor={isTagSelected(tag.name) ? selectedBg : borderColor}
                    borderWidth="2px"
                    _hover={{
                      bg: isTagSelected(tag.name) ? 'mamaFuel.moss.600' : 'gray.50',
                      transform: 'translateY(-1px)',
                      boxShadow: 'md'
                    }}
                    _active={{
                      transform: 'translateY(0px)',
                    }}
                    onClick={() => toggleMealTag(tag.name)}
                    height="auto"
                    py={2}
                    px={3}
                    whiteSpace="normal"
                    textAlign="center"
                  >
                    <Text>{tag.icon} {tag.name}</Text>
                  </Button>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MealPreferencesDialog;